import React from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useAppSelection } from '../contexts/AppSelectionContext';
import { useExamType } from '../contexts/ExamTypeContext';
import './Home.css';

const screenshots = [
  '/app-screenshots/1.png',
  '/app-screenshots/2.png',
  '/app-screenshots/3.png',
  '/app-screenshots/4.png',
  '/app-screenshots/5.png',
  '/app-screenshots/6.png',
  '/app-screenshots/7.png',
  '/app-screenshots/8.png',
  '/app-screenshots/9.png',
];

const Home: React.FC = () => {
  const { selectedApp, setShowModal } = useAppSelection();
  const { getExamTypeName } = useExamType();
  
  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    arrows: false,
    pauseOnHover: false,
  };

  // Get store links based on selection
  const androidLink = selectedApp?.storeLinks.android || 'https://play.google.com/store/apps/details?id=com.mhamada.lebenindeutschland';
  const iosLink = selectedApp?.storeLinks.ios || '#';

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="container hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Ace Your {selectedApp ? (
                <span className="hero-app-name">
                  {selectedApp.flag} German Citizenship Test
                </span>
              ) : 'German Citizenship Test (Einbürgerungstest)'}
            </h1>
            <p className="hero-subtitle">
              Master all 300 questions of the Leben in Deutschland exam with our comprehensive preparation app.
              Study in 8 languages, practice mock exams, and understand German history, culture, and politics with detailed explanations.
            </p>
            
            {selectedApp ? (
              <div className="download-buttons">
                <a
                  href={androidLink}
                  className="download-btn android-btn"
                  aria-label="Download on Google Play"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                  <div>
                    <span className="btn-label">GET IT ON</span>
                    <span className="btn-store">Google Play</span>
                  </div>
                </a>
                <a
                  href={iosLink}
                  className="download-btn ios-btn"
                  aria-label="Download on App Store"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                  </svg>
                  <div>
                    <span className="btn-label">Download on the</span>
                    <span className="btn-store">App Store</span>
                  </div>
                </a>
              </div>
            ) : (
              <button 
                className="select-app-btn"
                onClick={() => setShowModal(true)}
              >
                Download Now
              </button>
            )}
          </div>
          <div className="hero-image">
            <div className="phone-mockup-container">
              <img 
                src="/app-screenshots/apple-iphone-14.png" 
                alt="iPhone Frame" 
                className="phone-frame"
              />
              <div className="phone-screen">
                <Slider {...settings}>
                  {screenshots.map((src, index) => (
                    <div key={index} className="slider-image-container">
                      <img
                        src={src}
                        alt={`App Screenshot ${index + 1}`}
                        className="app-screenshot"
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">Everything You Need to Pass</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">📚</div>
              <h3>300 Official Questions</h3>
              <p>Study all 300 questions from the official Leben in Deutschland test pool, including state-specific questions for all 16 German states.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🌍</div>
              <h3>8 Languages</h3>
              <p>Practice in German, English, Arabic, Spanish, French, Russian, Turkish, or Farsi. All questions and explanations fully translated.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📝</div>
              <h3>Mock Exams</h3>
              <p>Take realistic 33-question practice tests that simulate the actual Einbürgerungstest. Track your progress and identify weak areas.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">💡</div>
              <h3>Detailed Explanations</h3>
              <p>Understand the "why" behind every answer with comprehensive explanations about German history, politics, society, and laws.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🔖</div>
              <h3>Bookmark & Review</h3>
              <p>Save difficult questions for later review. Automatically track mistakes and focus on questions you got wrong.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <h3>Progress Tracking</h3>
              <p>Monitor your study progress with detailed statistics. See your accuracy rate and how many questions you've mastered.</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about">
        <div className="container">
          <h2 className="section-title">About the Leben in Deutschland Test</h2>
          <div className="about-content">
            <p>
              The Leben in Deutschland test (Einbürgerungstest) is the official German citizenship test required for naturalization.
              To pass, you need to answer 17 out of 33 questions correctly. Questions cover German history, politics, society, laws,
              and culture. Our app provides the complete question pool of 300 general questions plus state-specific questions for your Bundesland.
            </p>
            <p>
              With detailed explanations in your language, contextual information to help you remember answers, and realistic mock exams,
              you'll be fully prepared to achieve German citizenship. Practice anytime, anywhere, and track your progress as you study.
            </p>
            <div className="stats">
              <div className="stat-item">
                <h3>300+ Questions</h3>
                <p>Complete Official Pool</p>
              </div>
              <div className="stat-item">
                <h3>8 Languages</h3>
                <p>Fully Translated</p>
              </div>
              <div className="stat-item">
                <h3>16 States</h3>
                <p>All Bundesländer</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Topics Section */}
      <section className="features">
        <div className="container">
          <h2 className="section-title">What You'll Learn</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">⚖️</div>
              <h3>German Law & Constitution</h3>
              <p>Learn about the Basic Law (Grundgesetz), fundamental rights, democratic principles, and the German legal system.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🏛️</div>
              <h3>History & Politics</h3>
              <p>Understand key historical events from World War II to reunification, and how the German government works today.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🤝</div>
              <h3>Society & Culture</h3>
              <p>Explore German social values, integration principles, equality, religious freedom, and cultural expectations.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🏢</div>
              <h3>State-Specific Knowledge</h3>
              <p>Master questions specific to your Bundesland including state symbols, history, and government structure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Start Your Journey to German Citizenship</h2>
          <p>Download the app and begin preparing for your Einbürgerungstest today</p>
          {selectedApp ? (
            <div className="download-buttons">
              <a 
                href={androidLink} 
                className="download-btn android-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <div>
                  <span className="btn-label">GET IT ON</span>
                  <span className="btn-store">Google Play</span>
                </div>
              </a>
              <a 
                href={iosLink} 
                className="download-btn ios-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z" />
                </svg>
                <div>
                  <span className="btn-label">Download on the</span>
                  <span className="btn-store">App Store</span>
                </div>
              </a>
            </div>
          ) : (
            <button 
              className="select-app-btn cta-select-btn"
              onClick={() => setShowModal(true)}
            >
              Download Now
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
